import { createSlice } from '@reduxjs/toolkit';
import { getApi, postApi } from '../../helpers/apiRequest/apiRequest';
import { addAsteriskValue, appendAsteriskIfStatusIsMandatory, getSubDomain, removeAsterisksIfEndsWithAsterisk, saveInLocalStorage } from '../../utils/common.service';
import {
  createProspect,
  getIdamToken,
  getWebsiteTemplate
} from '../../helpers/apiRequest/contractApiRequest';
import { DEFAULT_COUNTRY_CODE, DEFAULT_VALUE } from '../../constants/data';

export const APP_KEY = 'appKey';

export const initialState = {
  loading: false,
  companyWebsiteModel: {
    firstName: '',
    lastName: '',
    email: '',
    submitButtonText: '',
    bannerMessage: '',
    phoneNumber: '',
    title: '',
    fieldOneStatus: 0,
    fieldOneText: '',
    fieldTwoStatus: 0,
    fieldTwoText: '',
    fieldThreeStatus: 0,
    fieldThreeText: '',
    fieldFourStatus: 0,
    fieldFourText: '',
  },
  placeHolder: {
    fetchSiteDetailsirstName: null,
    lastName: null,
    email: null,
    submitButtonText: null,
    bannerMessage: null,
    phoneNumber: null,
    title: null,
    fieldOneText: null,
    fieldTwoText: null,
    fieldThreeText: null,
    fieldFourText: null,
  },
  logo: null,
  companyId: null,
  centerReferenceId: null,
  isPhoneFieldEnabled: false,
  isPhoneFieldMandatoryForProspects: false,
  isRightToLeftLanguageLayout: false,
  showTestResult: false,
  policyStatus: false,
  prospectId: null,
  openPolicyModal: false,
  defaultCurrency: null,
  error: null,
};

export const appSlice = createSlice({
  name: APP_KEY,
  initialState,
  reducers: {
    setDetails: (state, action) => {
      state.companyWebsiteModel = action.payload.companyWebsiteModel;
      state.isPhoneFieldEnabled = action.payload.isPhoneFieldEnabled;
      state.isPhoneFieldMandatoryForProspects = action.payload.isPhoneFieldMandatoryForProspects;
      if (state.isPhoneFieldMandatoryForProspects) {
        state.companyWebsiteModel.phoneNumber = `${state.companyWebsiteModel.phoneNumber}*`
      }
      state.companyWebsiteModel.fieldOneText = appendAsteriskIfStatusIsMandatory(state.companyWebsiteModel.fieldOneStatus, state.companyWebsiteModel.fieldOneText);
      state.companyWebsiteModel.fieldTwoText = appendAsteriskIfStatusIsMandatory(state.companyWebsiteModel.fieldTwoStatus, state.companyWebsiteModel.fieldTwoText);
      state.companyWebsiteModel.fieldThreeText = appendAsteriskIfStatusIsMandatory(state.companyWebsiteModel.fieldThreeStatus, state.companyWebsiteModel.fieldThreeText);
      state.companyWebsiteModel.fieldFourText = appendAsteriskIfStatusIsMandatory(state.companyWebsiteModel.fieldFourStatus, state.companyWebsiteModel.fieldFourText);
      state.isRightToLeftLanguageLayout = action.payload.isRightToLeftLanguageLayout;
      state.logo = action.payload.logoBlobUrl;
      const currency = DEFAULT_COUNTRY_CODE.find(e => e.countryName === action.payload.territoryName);
      state.defaultCurrency = currency
    },
    setCompanyId: (state, action) => {
      state.companyId = action.payload;
    },
    updateWebsiteInfo: (state, action) => {
      state.companyWebsiteModel = action.payload;
    },
    updateLoading: (state, action) => {
      state.loading = action.payload;
    },
    updatePlaceHolderDetails: (state, action) => {
      state.isPhoneFieldEnabled = action.payload.isPhoneFieldEnabled;
      state.isPhoneFieldMandatoryForProspects = action.payload.isPhoneFieldMandatoryForProspects
      state.isRightToLeftLanguageLayout = action.payload.isRightToLeftLanguageLayout;
      state.logo = action.payload.companyLogo;
      state.placeHolder = action.payload.placeHolders;
      if (action.payload.isPhoneFieldMandatoryForProspects) {
        state.placeHolder.PhoneNumber = `${state.placeHolder.phoneNumber} *`;
      }
      state.placeHolder.FieldOneText = appendAsteriskIfStatusIsMandatory(action.payload.placeHolders.fieldOneStatus, state.placeHolder.FieldOneText);
      state.placeHolder.FieldTwoText = appendAsteriskIfStatusIsMandatory(action.payload.placeHolders.fieldTwoStatus, state.placeHolder.FieldTwoText);
      state.placeHolder.FieldThreeText = appendAsteriskIfStatusIsMandatory(action.payload.placeHolders.fieldThreeStatus, state.placeHolder.FieldThreeText);
      state.placeHolder.FieldFourText = appendAsteriskIfStatusIsMandatory(action.payload.placeHolders.fieldFourStatus, state.placeHolder.FieldFourText);
      state.companyId = action.payload.companyId;
      state.centerReferenceId = action.payload.centerReferenceId;
      state.showTestResult = action.payload.showResults;
      state.defaultCurrency = DEFAULT_COUNTRY_CODE.find((option) => option.countryName === action.payload.territoryName);
    },
    updatePolicyStatus: (state, action) => {
      state.policyStatus = action.payload;
    },
    setProspectId: (state, action) => {
      state.prospectId = action.payload;
    },
    updateError: (state, action) => {
      state.error = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setCompanyId,
  setDetails,
  updateLoading,
  updateWebsiteInfo,
  updatePlaceHolderDetails,
  updatePolicyStatus,
  setProspectId,
  updateError,
} = appSlice.actions;

export const getCompanyDetails = (companyId) => {
  return async (dispatch) => {
    try {
      dispatch(updateLoading(true));
      const {
        companyWebsiteModel,
        isPhoneFieldEnabled,
        isRightToLeftLanguageLayout,
        isPhoneFieldMandatoryForProspects,
        logoBlobUrl,
        territoryName,
      } = await getApi(`Companies/${companyId}`);
      dispatch(
        setDetails({
          companyWebsiteModel,
          isPhoneFieldEnabled,
          isRightToLeftLanguageLayout,
          isPhoneFieldMandatoryForProspects,
          logoBlobUrl,
          territoryName,
        })
      );
      dispatch(updateLoading(false));
    } catch (err) {
      dispatch(updateLoading(false));
      if (err.response.status) {
        window.location.replace(`${process.env.REACT_APP_NSE_URL}`);
      }
    }
  };
};

export const updateCompanyDetails = (webSiteDetails) => {
  return async (dispatch) => {
    try {
      const requiredFields = DEFAULT_VALUE.requiredFields;
      if (webSiteDetails.isPhoneFieldMandatoryForProspects) {
        requiredFields.push('phoneNumber');
      }
      if (webSiteDetails.fieldOneStatus === 1) {
        requiredFields.push('fieldOneText');
      }
      if (webSiteDetails.fieldTwoStatus === 1) {
        requiredFields.push('fieldTwoText');
      }
      if (webSiteDetails.fieldThreeStatus === 1) {
        requiredFields.push('fieldThreeText');
      }
      if (webSiteDetails.fieldFourStatus === 1) {
        requiredFields.push('fieldFourText');
      }
      const companyModelValue = addAsteriskValue(webSiteDetails, requiredFields);
      dispatch(updateLoading(true));
      await postApi('Companies/website/details', {
        ...companyModelValue,
        phoneNumber: removeAsterisksIfEndsWithAsterisk(companyModelValue.phoneNumber),
        fieldOneText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldOneText),
        fieldTwoText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldTwoText),
        fieldThreeText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldThreeText),
        fieldFourText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldFourText)
      });
      dispatch(updateWebsiteInfo(companyModelValue));
      dispatch(updateLoading(false));
    } catch (err) {
      dispatch(updateLoading(false));
      window.location.replace(`${process.env.REACT_APP_NSE_URL}`);
    }
  };
};

export const saveCompanyDetails = (webSiteDetails) => {
  return async (dispatch) => {
    try {
      const requiredFields = DEFAULT_VALUE.requiredFields;
      const companyModelValue = addAsteriskValue(webSiteDetails, requiredFields);
      dispatch(updateLoading(true));
      await postApi('Companies/website/details', {
        ...companyModelValue,
        phoneNumber: removeAsterisksIfEndsWithAsterisk(companyModelValue.phoneNumber),
        fieldOneText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldOneText),
        fieldTwoText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldTwoText),
        fieldThreeText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldThreeText),
        fieldFourText: removeAsterisksIfEndsWithAsterisk(companyModelValue.fieldFourText)
      });
      dispatch(updateLoading(false));
      window.close()
    } catch (err) {
      dispatch(updateLoading(false));
      window.location.replace(`${process.env.REACT_APP_NSE_URL}`);
    }
  };
};

export const fetchSiteDetails = () => {
  return async (dispatch) => {
    try {
      const companyCode = getSubDomain(); 
      if (companyCode) {
        dispatch(updateLoading(true));
        const response = await getWebsiteTemplate(companyCode);
        const companyDetails = {
          placeHolders: response.placeHolders,
          companyId: response.companyId,
          companyLogo: response.companyLogo,
          showResults: response.showResults,
          isPhoneFieldEnabled: response.isPhoneFieldEnabled,
          isPhoneFieldMandatoryForProspects: response.isPhoneFieldMandatoryForProspects,
          isRightToLeftLanguageLayout: response.isRightToLeftLanguageLayout,
          centerReferenceId: response.centerReferenceId,
          territoryName: response.territoryName
        };
        saveInLocalStorage("wse.companyDetails", companyDetails);
        dispatch(updatePlaceHolderDetails(response));
        dispatch(updateLoading(false));

        window.dispatchEvent(new CustomEvent('companyDetailsLoaded', {
          detail: companyDetails
        }));
      }
    } catch (err) {
      dispatch(updateLoading(false));
    }
  };
};

export const loginProspect = (data) => {
  return async (dispatch) => {
    try {
      dispatch(updateLoading(true));
      const createProspectRes = await createProspect(data);
      dispatch(setProspectId(createProspectRes.id));
      dispatch(updateLoading(true));
      const getToken = await getIdamToken(createProspectRes.id);
      if (!getToken) {
        dispatch(updateLoading(false));
        return;
      }
      dispatch(updateLoading(false));
      const prospectApiUrl = process.env.REACT_APP_NSE_URL;
      const placementLoginUrl = `${prospectApiUrl}placementLogin/${getToken}`;
      window.location.replace(placementLoginUrl);
    } catch (err) {
      const errMessage = err?.response?.status === 400 ? 'User already exists' : 'Something went wrong';
      dispatch(updateError(errMessage));
      setTimeout(() => {
        dispatch(updateError(null))
      }, 5000)
      dispatch(updateLoading(false));
    }
  };
};

export const acceptPolicy = (prospectId, data) => {
  return async (dispatch) => {
    try {
      dispatch(updateLoading(true));
      dispatch(updatePolicyStatus(true));
      const getToken = await getIdamToken(prospectId);
      if (!getToken) {
        dispatch(updateLoading(false));
        return
      }
      const prospectApiUrl = process.env.REACT_APP_NSE_URL;
      dispatch(updateLoading(false));
      const placementLoginUrl = `${prospectApiUrl}placementLogin/${getToken}`;
      window.location.replace(placementLoginUrl);
    } catch (err) {
      dispatch(updateLoading(false));
    }
  };
};

export default appSlice.reducer;
